import type { ConversationListItemResponse } from "@redotech/effect-api-shared/models/shopper-ai/conversation/conversation";
import type { MessageChannel } from "@redotech/effect-api-shared/models/shopper-ai/conversation/message";
import { useMemo } from "react";
import { useChannelInConversation } from "./use-channel-in-conversation";

export interface UseConversationInfoCardProps {
  conversation: ConversationListItemResponse;
}

export interface UseConversationInfoCardResult {
  channels: MessageChannel[];
  isUnread: boolean;
  timeSinceMessage: string;
  lastMessageDate: Date | null;
}

/**
 * Hook to derive display data for a conversation info card.
 * - channels: derived from threads via useChannelInConversation
 * - isUnread: true if lastCustomerMessageAt > readAt
 * - timeSinceMessage: relative time string (e.g., "5m", "2h", "3d")
 * - lastMessageDate: the raw Date for tooltip display
 */
export function useConversationInfoCard({
  conversation,
}: UseConversationInfoCardProps): UseConversationInfoCardResult {
  const channels = useChannelInConversation(conversation.id);

  const { isUnread, timeSinceMessage, lastMessageDate } = useMemo(() => {
    const lastCustomerMessage = conversation.lastCustomerMessageAt;
    const readAt = conversation.readAt;

    // Determine if unread: has customer message after last read time
    const isUnread =
      lastCustomerMessage !== null &&
      (readAt === null || lastCustomerMessage > readAt);

    // Use the most recent message time (customer or merchant)
    const lastMessageDate =
      getMoreRecent(
        conversation.lastCustomerMessageAt,
        conversation.lastMerchantMessageAt,
      ) ?? null;

    const timeSinceMessage = lastMessageDate
      ? formatRelativeTime(lastMessageDate)
      : "";

    return { isUnread, timeSinceMessage, lastMessageDate };
  }, [
    conversation.lastCustomerMessageAt,
    conversation.lastMerchantMessageAt,
    conversation.readAt,
  ]);

  return { channels, isUnread, timeSinceMessage, lastMessageDate };
}

function getMoreRecent(a: Date | null, b: Date | null): Date | null {
  if (a === null) {
    return b;
  }
  if (b === null) {
    return a;
  }
  return a > b ? a : b;
}

/**
 * Format a date as a relative time string (e.g., "5m", "2h", "3d", "2w").
 */
function formatRelativeTime(date: Date): string {
  const now = Date.now();
  const diffMs = now - date.getTime();
  const diffSeconds = Math.floor(diffMs / 1000);

  if (diffSeconds < 60) {
    return "now";
  }

  const diffMinutes = Math.floor(diffSeconds / 60);
  if (diffMinutes < 60) {
    return `${diffMinutes}m`;
  }

  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) {
    return `${diffHours}h`;
  }

  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) {
    return `${diffDays}d`;
  }

  const diffWeeks = Math.floor(diffDays / 7);
  if (diffWeeks < 4) {
    return `${diffWeeks}w`;
  }

  const diffMonths = Math.floor(diffDays / 30);
  if (diffMonths < 12) {
    return `${diffMonths}mo`;
  }

  const diffYears = Math.floor(diffDays / 365);
  return `${diffYears}y`;
}
